import { ArrowLeftOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Descriptions,
  Drawer,
  Flex,
  Grid,
  Result,
  Table,
  Tabs,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { getMetricEffectiveStartDate } from '@/common/utils/metric-effective-period';
import { formatPayeeInfo } from '@/common/utils/payee-info';
import { queryStoreProfitReportCalculationNode } from '../services/profit-report';
import {
  appendFactReportDrilldownPath,
  getFactReportCalculationStoreText,
  getFactReportDrilldownColumns,
  getFactReportDrilldownTabs,
  getFactReportEvidencePagination,
  getFactReportEvidenceSection,
  getFactReportMetricDisplayName,
  getFactReportModelForecast,
  getFactReportStageText,
  popFactReportDrilldownPath,
} from '../utils/profit-report';

const DRILLDOWN_PAGE_SIZE = 20;
const CALCULATION_PAGE_SIZE = 50;
const DATE_FORMAT = 'YYYY-MM-DD';
const ALLOCATION_TYPE_LABELS = {
  none: '事件日生效',
  payment_to_month_end: '支付日至月末生效',
  previous_month: '上月生效',
  current_month: '本月生效',
  next_month: '下月生效',
  rental_next_month: '租期次月生效',
  specified_period: '指定周期生效',
};
const VALUE_TYPE_LABELS = { amount: '金额', quantity: '数量', ratio: '比率' };
const FORECAST_MODEL_LABELS = {
  linear_extrapolation: '线性外推',
  nearest_reuse: '就近复用',
};
const PERIOD_TYPE_LABELS = { day: '日', month: '月' };
const SOURCE_COLLECTION_TYPE_LABELS = {
  'feishu-bitable-operation-expense': '飞书多维表格',
  'hose-expense': '合思',
  'hose-loan': '合思',
  'hose-payment': '合思',
  'hose-requisition': '合思',
};
const renderText = (value) => value ?? '-';
const renderDate = (value) => {
  if (!value) return '-';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(DATE_FORMAT) : value;
};
const renderPlanPeriod = (_, record) =>
  `${renderDate(record.planStartedAt)} ~ ${renderDate(record.planEndedAt)}`;
const renderSalaryPeriod = (_, record) =>
  `${renderDate(record.salaryStartedAt)} ~ ${renderDate(record.salaryEndedAt)}`;
const getColumnRenderer = (format) => {
  if (format === 'date') return renderDate;
  if (format === 'planPeriod') return renderPlanPeriod;
  if (format === 'salaryPeriod') return renderSalaryPeriod;
  if (format === 'attributionPeriod') {
    return (_, record) =>
      `${getMetricEffectiveStartDate(record.attribution?.effectiveStartAt)?.format(DATE_FORMAT) ?? '-'} ~ ${getMetricEffectiveStartDate(record.attribution?.effectiveEndAt)?.format(DATE_FORMAT) ?? '-'}`;
  }
  if (format === 'payeeInfo') return formatPayeeInfo;
  if (format === 'allocationType') {
    return (value, record) =>
      ALLOCATION_TYPE_LABELS[record?.attribution?.sourceAllocationType ?? value] || '-';
  }
  if (format === 'sourceCollectionType') {
    return (value) => SOURCE_COLLECTION_TYPE_LABELS[value] || '-';
  }
  return renderText;
};

const createColumns = (sourceDomain, metric) =>
  getFactReportDrilldownColumns(sourceDomain, metric).map((column) => ({
    ...column,
    ellipsis: true,
    render:
      column.key === 'allocationDepartment'
        ? (value, record) => renderText(value ?? record.attribution?.targetStoreName)
        : getColumnRenderer(column.format),
  }));

const formatFormulaTokens = (tokens = []) =>
  tokens.length
    ? tokens.map((token) => (token.type === 'metric' ? token.name : token.value)).join(' ')
    : '-';

const formatCalculationExpression = (expression) => {
  if (!['bubble_available', 'first_available'].includes(expression?.type)) {
    return formatFormulaTokens(expression?.tokens);
  }
  const candidates = expression.candidates || [];
  const functionName =
    expression.type === 'bubble_available' ? 'BubbleAvailable' : 'FirstAvailable';
  return `${functionName}(${candidates
    .map((candidate) => candidate.name || candidate.metricId || candidate)
    .join(', ')})`;
};

const canOpenNode = (node, role) =>
  !(
    role === 'member' &&
    (node.attributes?.attributionType === 'headquarters_management_expense' ||
      node.dependencyRefs?.some(
        ({ attributionType }) => attributionType === 'headquarters_management_expense',
      ))
  ) &&
  (node.childCount > 0 ||
    node.sourceDomains?.length > 0 ||
    getFactReportModelForecast(node.forecast));

const createCalculationColumns = (onSelectNode, role, debug = false) =>
  [
    {
      key: 'store',
      render: (_, record) => getFactReportCalculationStoreText(record?.context),
      title: '门店/实体',
      width: 140,
    },
    {
      key: 'period',
      render: (_, record) => {
        const range = record?.context?.periodRange;
        return range ? `${renderDate(range.startDate)} ~ ${renderDate(range.endDate)}` : '-';
      },
      title: '周期',
      width: 210,
    },
    { dataIndex: 'name', key: 'name', title: '参与指标' },
    {
      dataIndex: 'valueType',
      key: 'valueType',
      render: (value) => VALUE_TYPE_LABELS[value] || '-',
      title: '值类型',
      width: 100,
    },
    {
      dataIndex: ['result', 'text'],
      key: 'value',
      render: renderText,
      title: '本周期值',
      width: 140,
    },
    {
      dataIndex: ['result', 'stage'],
      key: 'stage',
      render: getFactReportStageText,
      title: '值阶段',
      width: 90,
    },
    {
      key: 'action',
      render: (_, record) =>
        canOpenNode(record, role) ? (
          <Button
            aria-label={`查看${record.name}指标解释`}
            size="small"
            type="link"
            onClick={() => onSelectNode(record)}
          >
            查看
          </Button>
        ) : (
          '-'
        ),
      title: '操作',
      width: 90,
    },
  ].filter(({ key }) => debug || key !== 'stage');

const getCalculationResultItems = (node, debug = false) => [
  { children: node.result?.text || '-', key: 'value', label: '结果' },
  ...(debug
    ? [
        {
          children: getFactReportStageText(node.result?.stage),
          key: 'stage',
          label: '阶段',
        },
      ]
    : []),
  {
    children: VALUE_TYPE_LABELS[node.valueType] || '-',
    key: 'type',
    label: '值类型',
  },
];

const getNodeMetric = (node) => ({
  attributionType: node.attributes?.attributionType,
  drilldownDomains: node.sourceDomains || [],
  metricId: node.metricId,
  metricType: node.metricType,
  name: node.name,
  value: node.result,
  valueType: node.valueType,
});

const ProfitReportDrilldown = ({
  debug = false,
  identityKey,
  metric,
  onClose,
  open,
  organizationId,
  reportParams,
  role,
}) => {
  const screens = Grid.useBreakpoint();
  const [drilldownPath, setDrilldownPath] = useState([]);
  const [evidenceType, setEvidenceType] = useState();
  const [page, setPage] = useState(1);

  useEffect(() => {
    setDrilldownPath([]);
    setEvidenceType(undefined);
    setPage(1);
  }, [metric, open]);

  const params = {
    current: page,
    drilldownPath,
    ...reportParams,
    organizationId,
    pageSize: DRILLDOWN_PAGE_SIZE,
    rootMetricId: metric?.metricId,
  };
  const queryEnabled = Boolean(
    open && organizationId && identityKey && metric?.metricId && reportParams?.startDate,
  );
  const {
    data = {},
    isError,
    isFetching,
  } = useQuery({
    enabled: queryEnabled,
    queryFn: () => queryStoreProfitReportCalculationNode(params),
    queryKey: ['store-profit-report-calculation-node', identityKey, params],
  });
  const { evidenceSections = [], node } = data;
  const evidenceTabs = useMemo(
    () => getFactReportDrilldownTabs({ evidenceSections }),
    [evidenceSections],
  );
  useEffect(() => {
    if (evidenceTabs.length && !evidenceTabs.some(({ key }) => key === evidenceType)) {
      setEvidenceType(evidenceTabs[0].key);
      setPage(1);
    } else if (!evidenceTabs.length && evidenceType) {
      setEvidenceType(undefined);
    }
  }, [evidenceTabs, evidenceType]);

  const enterNode = (child) => {
    setDrilldownPath((current) =>
      appendFactReportDrilldownPath(current, child.drilldownPath.at(-1)),
    );
    setEvidenceType(undefined);
    setPage(1);
  };
  const leaveNode = () => {
    setDrilldownPath(popFactReportDrilldownPath);
    setEvidenceType(undefined);
    setPage(1);
  };
  const currentMetric = node ? getNodeMetric(node) : metric;
  const expressionText = formatCalculationExpression(node?.expression);
  const evidenceSection = getFactReportEvidenceSection(evidenceSections, evidenceType);
  const forecastEvidenceSection = getFactReportEvidenceSection(evidenceSections, 'forecast');
  const modelForecast = getFactReportModelForecast(node?.forecast);

  let content = null;
  if (isError) {
    content = <Alert showIcon title="指标解释加载失败，请刷新报表后重试" type="error" />;
  } else if (!queryEnabled) {
    content = <Result status="info" subTitle="请刷新报表后重试" title="计算依据已失效" />;
  } else if (node) {
    content = (
      <Flex vertical gap="middle">
        <Descriptions
          bordered
          column={{ xs: 1, sm: debug ? 3 : 2 }}
          items={getCalculationResultItems(node, debug)}
          size="small"
        />
        {node.expression ? (
          <Typography.Text>
            <Typography.Text strong>公式：</Typography.Text>
            {expressionText}
          </Typography.Text>
        ) : null}
        {node.children?.length ? (
          <Table
            columns={createCalculationColumns(enterNode, role, debug)}
            dataSource={node.children}
            pagination={{
              hideOnSinglePage: true,
              pageSize: CALCULATION_PAGE_SIZE,
              showSizeChanger: false,
            }}
            rowKey={(record) => `${record.drilldownPath.join('.')}:${record.metricId}`}
            scroll={{ x: 'max-content' }}
            size="small"
          />
        ) : null}
        {modelForecast ? (
          <Flex vertical gap="middle">
            <Descriptions
              bordered
              column={{ xs: 1, sm: 2 }}
              items={[
                {
                  children: FORECAST_MODEL_LABELS[modelForecast.modelType] || '-',
                  key: 'model',
                  label: '推测模型',
                },
                {
                  children: PERIOD_TYPE_LABELS[modelForecast.periodType] || '-',
                  key: 'period',
                  label: '指标周期',
                },
              ]}
              size="small"
            />
            <Table
              columns={[
                {
                  key: 'range',
                  render: (_, sample) => `${sample.startDate} ~ ${sample.endDate}`,
                  title: '历史周期',
                },
                { dataIndex: 'value', key: 'value', title: '样本值' },
              ]}
              dataSource={forecastEvidenceSection.data || []}
              pagination={getFactReportEvidencePagination(forecastEvidenceSection, page)}
              rowKey="index"
              size="small"
              onChange={(pagination) => setPage(pagination.current || 1)}
            />
          </Flex>
        ) : null}
        {evidenceTabs.length ? (
          <Tabs
            activeKey={evidenceType}
            items={evidenceTabs.map((tab) => ({
              ...tab,
              children:
                tab.key === evidenceType ? (
                  <Table
                    columns={createColumns(evidenceType, currentMetric)}
                    dataSource={evidenceSection.data || []}
                    loading={isFetching}
                    pagination={getFactReportEvidencePagination(evidenceSection, page)}
                    rowKey="id"
                    scroll={{ x: 'max-content' }}
                    size="small"
                    onChange={(pagination) => setPage(pagination.current || 1)}
                  />
                ) : null,
            }))}
            onChange={(value) => {
              setEvidenceType(value);
              setPage(1);
            }}
          />
        ) : null}
      </Flex>
    );
  }

  const title = (
    <Flex align="center" gap="small">
      {drilldownPath.length ? (
        <Button
          aria-label="返回上一级指标解释"
          icon={<ArrowLeftOutlined />}
          type="text"
          onClick={leaveNode}
        />
      ) : null}
      <Typography.Text>
        {getFactReportMetricDisplayName(currentMetric?.name)}指标解释
      </Typography.Text>
    </Flex>
  );

  return (
    <Drawer
      destroyOnHidden
      loading={isFetching && !node}
      open={open}
      size={screens.md ? 'min(1024px, 100vw)' : '100%'}
      title={title}
      onClose={onClose}
    >
      {content}
    </Drawer>
  );
};

ProfitReportDrilldown.propTypes = {
  debug: PropTypes.bool,
  identityKey: PropTypes.string.isRequired,
  metric: PropTypes.shape({
    metricId: PropTypes.string,
    name: PropTypes.string,
    value: PropTypes.shape({ stage: PropTypes.string, text: PropTypes.string }),
  }),
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  organizationId: PropTypes.string.isRequired,
  reportParams: PropTypes.shape({
    endDate: PropTypes.string.isRequired,
    scope: PropTypes.shape({ type: PropTypes.string.isRequired }).isRequired,
    startDate: PropTypes.string.isRequired,
  }).isRequired,
  role: PropTypes.string,
};

export {
  createCalculationColumns,
  createColumns,
  formatCalculationExpression,
  formatFormulaTokens,
  getCalculationResultItems,
};
export default ProfitReportDrilldown;
